import {
  Component,
  forwardRef,
  inject,
  input,
  model,
  OnInit,
  output,
  viewChild,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, filter } from 'rxjs';

import { CustomerLocation } from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { AppInputDirective } from '../../../../legacy/form/controls/input/input.directive';
import {
  AutoCompleteFieldComponent,
  AutoCompleteFieldComponent as AutoCompleteFieldComponent_1
} from '../auto-complete-field/auto-complete-field.component';
import { LocationSearchService } from '../../../../../services/location-search.service';
import { LocationSearchResultEntry } from '../../../../../queries/location-search.queries';
import { AppFormFieldControl } from '../../../../legacy/form/form-field/form-field-control/form-field-control';
import { BaseControl } from '../../../../legacy/form/controls/base-control';

@Component({
  selector: 'app-location-search-field',
  templateUrl: './location-search-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSearchFieldComponent),
      multi: true
    },
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => LocationSearchFieldComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AutoCompleteFieldComponent_1,
    AppInputDirective,
    FormsModule,
    TranslatePipe
  ]
})
export class LocationSearchFieldComponent
  extends BaseControl<string>
  implements OnInit
{
  locationSearch = inject(LocationSearchService);
  #destroyRef = inject(DestroyRef);
  readonly ngControl = viewChild(NgControl);
  readonly error = model(false);
  readonly removeLocality = input(false);
  readonly placeholder = input('general.location_search');
  readonly country = input<CustomerLocation>(CustomerLocation.DE);
  readonly appendCountryToSearch = input(false);
  readonly locationData = output<LocationSearchResultEntry>();

  readonly autoCompleteFieldComponent = viewChild(AutoCompleteFieldComponent);

  public loading = false;
  public locationSearchSelector$ = new BehaviorSubject<any>(null);

  private recentlySelected = false;

  public get getLocationSearchControlObservable() {
    return this.ngControl().statusChanges.pipe(
      filter(() => {
        if (this.recentlySelected) {
          this.recentlySelected = false;
          return false;
        }
        return !this.ngControl().pristine;
      })
    );
  }

  ngOnInit(): void {
    this.ngControl().statusChanges.subscribe(() => this.stateChanges.next());
  }

  public onNewInput() {
    let value = this.value;
    if (!this.recentlySelected) {
      const country = this.country();
      if (country && this.appendCountryToSearch()) {
        // We append the country to apiGeoSearch to get results from DE/AT
        // For this we have to use german names for the countries
        switch (country) {
          case CustomerLocation.DE:
            value = value?.concat(' Deutschland');
            break;
          case CustomerLocation.AT:
            value = value?.concat(' Österreich');
            break;
          default:
            break;
        }
      }
      this.loading = true;
      this.locationSearch
        .getLocations$(value)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(
          data => {
            const features = data.data.searchLocations.features;
            const results = this.removeLocality()
              ? features.filter(
                  feature => feature.properties?.type !== 'locality'
                )
              : features;

            this.locationSearchSelector$.next(results);
            this.loading = false;
          },
          error => this.error.set(error)
        );
    }
    this.recentlySelected = false;
  }

  public onSelect(event: LocationSearchResultEntry) {
    this.value = this.locationSearch.getLocationLabel(event.properties, true);
    this.loading = false;
    this.recentlySelected = true;
    this.locationData.emit(event);
  }

  public onFocusOut() {
    if (this.ngControl()?.control?.errors) {
      this.stateChanges.next();
    }
  }

  // Should silently reset the value, without opening the dropdown
  public resetValue() {
    this.ngControl().reset(undefined);
  }
}
